let fontEpoch=0;
const fittedFaces=new WeakMap<HTMLElement,string>();
const fittingAsync=new WeakSet<HTMLElement>();
/** Resolve a face before it becomes visible, using the same resize rules. */
export function fitCardText(face: HTMLElement,prepareHidden=false): void {
  if(fittingAsync.has(face))return;
  for(const _ of fitTextSteps(face,prepareHidden)){/* Synchronous details/idle cards. */}
}
/** Fit a still-hidden face during the card's travel, one layout step per frame. */
export async function fitCardTextDuringMotion(face:HTMLElement,valid:()=>boolean):Promise<void> {
  if(typeof requestAnimationFrame!=='function'){fitCardText(face,true);return;}
  fittingAsync.add(face);
  try{
    for(const _ of fitTextSteps(face,true)){
      await new Promise<void>(done=>requestAnimationFrame(()=>done()));
      if(!valid() || !face.isConnected){fittedFaces.delete(face);return;}
    }
  }finally{fittingAsync.delete(face);}
}
function* fitTextSteps(face:HTMLElement,prepareHidden:boolean):Generator<void> {
  if(face.clientWidth<10 || face.clientHeight<10 || (!prepareHidden && face.closest('.question-pending')))return;
  const key=[face.clientWidth,face.clientHeight,face.className,face.textContent,face.parentElement?.className,face.closest('.draw-interaction')?.className,fontEpoch].join('|');
  if(fittedFaces.get(face)===key)return;
  fittedFaces.set(face,key);
  if(face.classList.contains('pile-card-tab')){
    const name=face.querySelector<HTMLElement>('.event-tab-title');
    if(name){name.style.removeProperty('font-size');let size=parseFloat(getComputedStyle(name).fontSize);
      while(name.scrollWidth>name.clientWidth+1 && size>6){size-=.25;name.style.fontSize=`${size}px`;}}
    return;
  }
  // Lost cards intentionally overlap. Fit the exposed name horizontally;
  // tapping still opens the complete bilingual face.
  if(face.closest('.pile-paper')) {
    const scoped=!!face.closest('.tabletop[data-hand-context="setback"]');
    const name=face.querySelector<HTMLElement>(':scope > strong');
    if(name){name.style.removeProperty('font-size');let size=parseFloat(getComputedStyle(name).fontSize);
      while(name.scrollWidth>name.clientWidth+1 && size>(scoped?4:6)){size-=.25;name.style.fontSize=`${size}px`;}}
    if(scoped){
      const english=face.querySelector<HTMLElement>(':scope > .en');
      if(english && name){
        english.style.top=`${name.offsetTop+name.offsetHeight+3}px`;
        english.style.removeProperty('font-size');let size=parseFloat(getComputedStyle(english).fontSize);
        while(english.offsetTop+english.offsetHeight>face.clientHeight-6 && size>3){size-=.25;english.style.fontSize=`${size}px`;}
      }
    }
    return;
  }
  const nodes=[...face.querySelectorAll<HTMLElement>(':scope > strong, :scope > .en, :scope > .card-title-en, :scope > p, :scope > strong > .en, :scope > p > .en, .detail-card-copy p')];
  nodes.forEach(node=>node.style.removeProperty('font-size'));
  const bases=nodes.map(node=>parseFloat(getComputedStyle(node).fontSize));
  const apply=(scale:number)=>nodes.forEach((node,i)=>{node.style.fontSize=`${bases[i]!*scale}px`;});
  const css=getComputedStyle(face);
  const children=[...face.children].filter((child):child is HTMLElement=>child instanceof HTMLElement && !['absolute','fixed'].includes(getComputedStyle(child).position) && getComputedStyle(child).display!=='none');
  const padding=parseFloat(css.paddingTop)+parseFloat(css.paddingBottom);
  const rowGap=Math.max(0,children.length-1)*(parseFloat(css.rowGap)||0);
  const margin=children.reduce((sum,child)=>{const c=getComputedStyle(child);return sum+parseFloat(c.marginTop)+parseFloat(c.marginBottom);},0);
  const fits=()=>{
    const height=children.reduce((sum,child)=>sum+child.offsetHeight,0)+margin+rowGap;
    return height<=face.clientHeight-padding-1 && face.scrollHeight<=face.clientHeight && face.scrollWidth<=face.clientWidth;
  };
  yield;
  if(fits())return;
  let low=.25,high=1;
  for(let i=0;i<8;i++){const mid=(low+high)/2;apply(mid);yield;if(fits())low=mid;else high=mid;}
  apply(low);
  // Font rounding and flex layout can settle one line differently at the
  // chosen boundary. Leave a small safety margin inside the printed frame.
  for(let i=0;i<8 && !fits();i++){low*=.97;apply(low);yield;}
}

/** Fit actual text, including translations, without changing card geometry. */
export class CardTextLayout {
  private readonly mutations: MutationObserver;
  private readonly sizes: ResizeObserver;
  private readonly faces = new Set<HTMLElement>();
  private queued = false;
  private dirty = true;
  private work:HTMLElement[]=[];
  private cursor=0;
  private disposed = false;
  constructor(private readonly root: HTMLElement) {
    this.sizes = new ResizeObserver(() => this.schedule());
    this.mutations = new MutationObserver(() => this.schedule());
    this.mutations.observe(root, {childList:true, subtree:true, characterData:true});
    void document.fonts?.ready.then(() => {fontEpoch++;this.schedule();});
    this.schedule();
  }
  private schedule(): void {
    this.dirty=true;this.queueFrame();
  }
  private queueFrame():void {
    if(this.queued || this.disposed)return;
    this.queued=true;
    requestAnimationFrame(() => {
      this.queued=false;if(this.disposed)return;
      if(this.dirty){this.dirty=false;this.work=this.prepare();this.cursor=0;}
      const start=performance.now();
      while(this.cursor<this.work.length){
        const face=this.work[this.cursor++]!;if(this.root.contains(face))fitCardText(face);
        // A newly resized hand must not block the card's entire flip frame.
        if(performance.now()-start>=6)break;
      }
      if(this.cursor<this.work.length || this.dirty)this.queueFrame();
    });
  }
  private prepare(): HTMLElement[] {
    for(const hand of this.root.querySelectorAll<HTMLElement>('.tabletop[data-hand-context="setback"] .hand-held')){
      const cards=[...hand.querySelectorAll<HTMLElement>(':scope > .table-hand-card')];
      if(!cards.length)continue;
      const css=getComputedStyle(hand),gapX=parseFloat(css.columnGap)||0,gapY=parseFloat(css.rowGap)||0;
      const w=hand.clientWidth-parseFloat(css.paddingLeft)-parseFloat(css.paddingRight);
      const h=hand.clientHeight-parseFloat(css.paddingTop)-parseFloat(css.paddingBottom);
      const mobile=matchMedia('(max-width:700px)').matches;
      const columns=Math.min(6,cards.length);
      const rows=Math.ceil(cards.length/columns),ratio=mobile?2/3:83/116;
      const width=Math.min(140,(w-(columns-1)*gapX)/columns,((h-(rows-1)*gapY)/rows)*ratio);
      hand.style.gridTemplateColumns=`repeat(${columns},minmax(0,1fr))`;
      hand.style.gridTemplateRows=`repeat(${rows},minmax(0,1fr))`;
      for(const card of cards){card.style.width=`${width}px`;card.style.height=`${width/ratio}px`;}
    }
    // Reserve the complete last paper, rather than a fixed 27px tail. Keep
    // every exposed name tappable, including when two stacks share the dock.
    for(const aside of this.root.querySelectorAll<HTMLElement>('.tabletop[data-hand-context="setback"] .hand-piles')) {
      const stacks=[...aside.querySelectorAll<HTMLElement>('.hand-pile-stack')].filter(s=>s.clientHeight>=10);
      if(!stacks.length)continue;
      const measurements=stacks.map(stack=>{
        const pile=stack.parentElement!,rows=Number(pile.dataset.pileRows),columns=Number(pile.dataset.pileColumns);
        return {stack,pile,rows,step:pile.classList.contains('accepted-back-pile')?24:14,columnWidth:(stack.clientWidth-6-(columns-1)*5)/columns-6,overhead:pile.getBoundingClientRect().height-stack.getBoundingClientRect().height};
      });
      const css=getComputedStyle(aside);
      const available=aside.clientHeight-parseFloat(css.paddingTop)-parseFloat(css.paddingBottom)
        -(parseFloat(css.rowGap)||0)*(stacks.length-1)-measurements.reduce((n,m)=>n+m.overhead+4,0);
      const steps=measurements.reduce((n,m)=>n+(m.rows-1)*m.step,0);
      const width=Math.max(12,Math.min(...measurements.map(m=>m.columnWidth),(available-steps)/(1.5*stacks.length)));
      for(const {stack,pile,rows,step,overhead} of measurements){
        pile.style.flex=`0 0 ${overhead+(rows-1)*step+width*1.5+4}px`;
        stack.style.gridTemplateRows=`${rows>1?`repeat(${rows-1},minmax(0,1fr)) `:''}${width*1.5}px`;
        stack.style.setProperty('--fitted-paper-width',`${width}px`);
        stack.style.setProperty('--fitted-paper-height',`${width*1.5}px`);
      }
    }
    for(const face of this.faces)if(!this.root.contains(face)) {this.sizes.unobserve(face);this.faces.delete(face);}
    const visible=[...this.root.querySelectorAll<HTMLElement>('.life-card, .paper-front, .pile-card-tab:has(.event-tab-title)')];
    for(const face of visible) {
      if(!this.faces.has(face)){this.faces.add(face);this.sizes.observe(face);}
    }
    // The central card gets its text fitted before less prominent hand cards.
    return visible.sort((a,b)=>Number(b.classList.contains('paper-front'))-Number(a.classList.contains('paper-front')));
  }
  dispose(): void {this.disposed=true;this.mutations.disconnect();this.sizes.disconnect();this.faces.clear();this.work=[];}
}
